import { useEffect, useMemo, useState } from "react";
import type { ClientMessage } from "@shared/protocol";
import { useAgentTerminal } from "../hooks/useAgent";
import { useAppStore, type PanelState } from "../store/appStore";
import { Ico } from "./icons";
import { StatusDot } from "./atoms";

interface Props {
  panel: PanelState;
  send: (msg: ClientMessage) => boolean;
  /** Sink registry — App listens for agent:output and calls writers[agentId]?.write. */
  registerWriter: (agentId: string, write: (data: string) => void) => () => void;
}

const RECAP_RE = /recap:\s*(.+)/i;

function runtime(startedAt: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

export function TerminalPanel({ panel, send, registerWriter }: Props) {
  const [, force] = useState(0);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const setActivePanel = useAppStore((s) => s.setActivePanel);
  const removePanel = useAppStore((s) => s.removePanel);
  const patchPanel = useAppStore((s) => s.patchPanel);

  const binding = useMemo(
    () => ({ agentId: panel.agentId, readonly: panel.readonly ?? false, send }),
    [panel.agentId, panel.readonly, send]
  );

  const term = useAgentTerminal(container, binding);

  useEffect(() => {
    if (!term) return;
    const unregister = registerWriter(panel.agentId, (data: string) => {
      term.write(data);
      const match = data.match(RECAP_RE);
      if (match?.[1]) patchPanel(panel.agentId, { recap: match[1] });
    });
    // ask the server to replay scrollback
    send({ type: "agent:attach", payload: { agentId: panel.agentId } });
    return unregister;
  }, [panel.agentId, term, registerWriter, patchPanel, send]);

  // Tick the runtime label every second.
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const close = () => {
    send({ type: "agent:kill", payload: { agentId: panel.agentId } });
    removePanel(panel.agentId);
  };

  return (
    <section
      className={`panel ${panel.active ? "active" : ""} ${panel.readonly ? "readonly" : ""}`}
      onClick={() => setActivePanel(panel.agentId)}
    >
      <div className="panel-header">
        <StatusDot status={panel.status} />
        <div className="panel-title">
          <span className="task">{panel.task}</span>
          <span className="runtime">{runtime(panel.startedAt)}</span>
          {panel.readonly && <span className="readonly-tag">readonly</span>}
        </div>
        <div className="panel-actions">
          <button title="Maximize"><Ico name="expand" size={14} /></button>
          <button title="Split"><Ico name="split" size={14} /></button>
          <button title="More"><Ico name="more" size={14} /></button>
          <button title="Schließen" className="danger" onClick={close}><Ico name="x" size={14} /></button>
        </div>
      </div>

      <div className="terminal-host" ref={setContainer} />

      {!panel.readonly && (
        <div className="panel-input">
          <input
            placeholder="Nachricht an Claude — Enter zum Senden"
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              const value = e.currentTarget.value;
              if (!value) return;
              const ok = send({
                type: "agent:input",
                payload: { agentId: panel.agentId, data: value + "\r" },
              });
              if (ok) {
                e.currentTarget.value = "";
              } else {
                // Visible signal that the WS wasn't open and the message
                // didn't go anywhere — otherwise the user types into the
                // void.
                e.currentTarget.style.borderColor = "var(--color-error)";
                setTimeout(() => { e.currentTarget.style.borderColor = ""; }, 600);
              }
            }}
          />
          <span className="mode">auto · shift+tab</span>
        </div>
      )}

      {panel.recap && (
        <div className="recap-bar">
          <Ico name="sparkles" size={12} className="recap-icon" />
          <div className="recap-text"><b>Recap:</b>{panel.recap}</div>
        </div>
      )}
    </section>
  );
}
